import { useState, type ReactNode } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import { getLocale, type Locale } from '../../../i18n';
import { compact, percentDelta } from '../../../i18n/format';
import { Absent, EstimateLabel, ProviderFidelity } from '../../../chrome/measure';
import { count } from '../../../chrome/measure/format';
import { StatCard, StatGrid } from '../../../chrome/page';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../ui/chart';
import { Toggle } from '../../../ui/toggle';
import { LoadingState } from '../../../chrome/state';
import type { PostMetricRow, PostReach } from '../api/schemas';
import type { Reading, Score } from '../publications/readings';
import { latestMetrics } from './compute';
import { pub } from './messages';

/** A headline score names its direction, not the research-grade decimal. */
function wholeScore(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' })
    .format(value)
    .replace(/^-/, '−');
}

function DeltaChip(props: { delta: number }): ReactNode {
  const tone =
    props.delta > 0
      ? 'bg-primary-quiet text-primary-quiet-foreground'
      : props.delta < 0
        ? 'bg-destructive-quiet text-destructive-quiet-foreground'
        : 'bg-secondary text-foreground';
  return (
    <span
      data-numeric
      data-slot="delta"
      className={`text-caption rounded-pill px-2 py-0.5 ${tone}`}
    >
      {percentDelta(props.delta * 100, getLocale())}
      <span className="sr-only"> {pub('figure.versus')}</span>
    </span>
  );
}

function ofReading<T>(reading: Reading<T>, draw: (value: T) => ReactNode): ReactNode {
  if (reading.status === 'loading') return <LoadingState lines={1} />;
  if (reading.status === 'failed') return <Absent reason={{ text: pub('figure.failed') }} />;
  return draw(reading.value);
}

/** The post's headline figures. A reach or share reach the route has no
 *  reading of is left out rather than drawn as an absence. */
export function MetricsGrid(props: {
  figures: PostReach | null;
  sentiment: Reading<Score | null>;
  comments: Reading<number>;
}): ReactNode {
  const locale = getLocale();
  const { figures } = props;
  const reach = figures?.reach ?? null;
  const delta = figures?.reach_delta ?? null;
  const estimate = figures?.share_reach ?? null;
  return (
    <StatGrid>
      {reach !== null && (
        <StatCard
          label={pub('figure.reach')}
          value={compact(reach, locale)}
          chip={delta === null ? undefined : <DeltaChip delta={delta} />}
        />
      )}
      {estimate !== null && estimate.value !== null && (
        <StatCard
          label={pub('figure.shareReach')}
          value={compact(estimate.value, locale)}
          chip={
            <EstimateLabel
              methodVersion={estimate.method_version}
              confidence={estimate.confidence}
            />
          }
        />
      )}
      <StatCard
        label={pub('figure.sentiment')}
        value={ofReading(props.sentiment, (score) =>
          score === null ? (
            <Absent reason={{ text: pub('figure.noScore') }} />
          ) : (
            wholeScore(score.score, locale)
          ),
        )}
      />
      <StatCard
        label={pub('figure.comments')}
        value={ofReading(props.comments, (total) => count(total, locale))}
      />
    </StatGrid>
  );
}

/** A metrics tool result as the assistant receives it: the latest observation
 *  of each metric per provider, each with the provider it came from. */
export function ProviderMetricsGrid(props: { rows: readonly PostMetricRow[] }): ReactNode {
  return (
    <StatGrid>
      {latestMetrics(props.rows).map((row) => (
        <StatCard
          key={`${row.metric}-${row.provider}`}
          label={pub(`metric.${row.metric}`)}
          value={
            row.value === null ? <Absent reason="not_measured" /> : count(row.value, getLocale())
          }
          chip={<ProviderFidelity provider={row.provider} fidelity={row.fidelity} />}
        />
      ))}
    </StatGrid>
  );
}

type HistoryMetric = PostMetricRow['metric'];

/** Counts share the left axis; reach and views run orders of magnitude higher
 *  and take the right, dashed, so the axis a line reads against is visible. */
const LOW_METRICS: readonly HistoryMetric[] = ['comments', 'likes', 'shares', 'saves'];
const HIGH_METRICS: readonly HistoryMetric[] = ['estimated_reach', 'views'];

const SERIES_COLOR: Record<HistoryMetric, string> = {
  comments: 'var(--chart-1)',
  likes: 'var(--chart-2)',
  shares: 'var(--chart-3)',
  saves: 'var(--chart-4)',
  views: 'var(--chart-5)',
  estimated_reach: 'var(--foreground)',
};

type HistoryDatum = { at: number } & Partial<Record<HistoryMetric, number>>;

/** The observations as one row per observed instant, keeping only the metrics
 *  the post has a reading of: a null the network returned is not a point. */
export function historyOf(rows: readonly PostMetricRow[]): {
  series: HistoryMetric[];
  data: HistoryDatum[];
} {
  const shown = rows.filter((row) => row.value !== null);
  const series = [...LOW_METRICS, ...HIGH_METRICS].filter((metric) =>
    shown.some((row) => row.metric === metric),
  );
  const byInstant = new Map<number, HistoryDatum>();
  for (const row of shown) {
    const at = Date.parse(row.observed_at);
    byInstant.set(at, { ...byInstant.get(at), at, [row.metric]: row.value });
  }
  return { series, data: [...byInstant.values()].sort((a, b) => a.at - b.at) };
}

function instant(at: number): string {
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
}

/** Every collected metric over the window as one chart: counts against the
 *  left axis, reach and views against the right. The legend switches a line
 *  off and on; the tooltip lists every line at the hovered instant. */
export function MetricHistory(props: { rows: readonly PostMetricRow[] }): ReactNode {
  const locale = getLocale();
  const [hidden, setHidden] = useState<ReadonlySet<HistoryMetric>>(new Set());
  const { series, data } = historyOf(props.rows);
  const config: ChartConfig = Object.fromEntries(
    series.map((metric) => [
      metric,
      { label: pub(`metric.${metric}`), color: SERIES_COLOR[metric] },
    ]),
  );
  const low = series.some((metric) => LOW_METRICS.includes(metric));
  const high = series.some((metric) => HIGH_METRICS.includes(metric));
  const axisTick = (value: number) => compact(value, locale);

  return (
    <div className="flex flex-col gap-3">
      <ChartContainer
        config={config}
        role="img"
        aria-label={pub('history.chart')}
        initialDimension={{ width: 700, height: 288 }}
        className="aspect-auto h-72 w-full"
      >
        <LineChart data={data} margin={{ top: 8, left: 4, right: 4 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="at"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={instant}
            tickLine={false}
            axisLine={false}
            minTickGap={32}
            padding={{ left: 12, right: 12 }}
          />
          {low && (
            <YAxis
              yAxisId="low"
              orientation="left"
              tickFormatter={axisTick}
              tickLine={false}
              axisLine={false}
              width={44}
            />
          )}
          {high && (
            <YAxis
              yAxisId="high"
              orientation="right"
              tickFormatter={axisTick}
              tickLine={false}
              axisLine={false}
              width={44}
            />
          )}
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) => {
                  const at = (payload[0]?.payload as HistoryDatum | undefined)?.at;
                  return at === undefined ? null : instant(at);
                }}
              />
            }
          />
          {series.map((metric) => {
            const right = HIGH_METRICS.includes(metric);
            return (
              <Line
                key={metric}
                dataKey={metric}
                yAxisId={right ? 'high' : 'low'}
                stroke={SERIES_COLOR[metric]}
                strokeWidth={2}
                strokeDasharray={right ? '6 4' : undefined}
                dot={{ r: 2 }}
                connectNulls
                hide={hidden.has(metric)}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ChartContainer>
      <div role="group" aria-label={pub('history.legend')} className="flex flex-wrap gap-2">
        {series.map((metric) => (
          <Toggle
            key={metric}
            size="sm"
            variant="outline"
            pressed={!hidden.has(metric)}
            onPressedChange={(on) =>
              setHidden((current) => {
                const next = new Set(current);
                if (on) next.delete(metric);
                else next.add(metric);
                return next;
              })
            }
          >
            <span
              aria-hidden
              className="h-0.5 w-3 rounded-pill"
              style={{
                background: HIGH_METRICS.includes(metric)
                  ? `repeating-linear-gradient(90deg, ${SERIES_COLOR[metric]} 0 4px, transparent 4px 6px)`
                  : SERIES_COLOR[metric],
              }}
            />
            {pub(`metric.${metric}`)}
            {HIGH_METRICS.includes(metric) && high && low && (
              <span className="text-muted-foreground">{pub('history.rightAxis')}</span>
            )}
          </Toggle>
        ))}
      </div>
    </div>
  );
}
